import { Command } from 'commander';
import chalk from 'chalk';
import inquirer from 'inquirer';
import { WorkflowVisualizer, VisualizationOptions } from '../core/workflow-visualizer';
import { WorkflowManager } from '../core/workflow-manager';
import { TaskStore } from '../core/task-store';
import { AgentCollaborationManager } from '../core/agent-collaboration';
import { AgentManager } from '../core/agent-manager';
import { WorkflowEngine } from '../core/workflow/workflow-engine';
import { logger } from '../utils/logger';

export function createVisualizeCommand(): Command {
  const command = new Command('visualize');
  
  command
    .description('可视化工作流、任务依赖和协作关系')
    .alias('viz')
    .addCommand(createWorkflowCommand())
    .addCommand(createTasksCommand())
    .addCommand(createCollaborationCommand())
    .addCommand(createDependenciesCommand());
  
  return command;
}

function createWorkflowCommand(): Command {
  const command = new Command('workflow');
  
  command
    .description('可视化工作流定义')
    .argument('[workflowId]', '工作流 ID')
    .option('-f, --format <format>', '输出格式 (ascii|mermaid|graphviz|json)', 'ascii')
    .option('-d, --details', '显示详细信息')
    .option('-c, --conditions', '显示条件')
    .option('-o, --output <file>', '输出到文件')
    .option('--no-color', '禁用颜色')
    .option('-O, --orientation <orientation>', '布局方向 (horizontal|vertical)', 'vertical')
    .action(async (workflowId, options) => {
      try {
        const workflowManager = new WorkflowManager();
        const visualizer = new WorkflowVisualizer();
        
        // 如果没有提供 ID，列出所有工作流让用户选择
        if (!workflowId) {
          const workflows = await workflowManager.getWorkflowList();
          
          if (workflows.length === 0) {
            console.log(chalk.yellow('没有找到工作流'));
            return;
          }
          
          const { selectedId } = await inquirer.prompt({
            type: 'list',
            name: 'selectedId',
            message: '选择要可视化的工作流:',
            choices: workflows.map(w => ({
              name: `${w.name} - ${w.description}`,
              value: w.id
            }))
          });
          
          workflowId = selectedId;
        }
        
        // 加载工作流
        const workflow = await workflowManager.loadWorkflow(workflowId);
        if (!workflow) {
          console.log(chalk.red(`工作流不存在: ${workflowId}`));
          return;
        }
        
        // 转换为 WorkflowDefinition
        const engine = new WorkflowEngine();
        const definition = {
          id: workflow.id,
          name: workflow.name,
          description: workflow.description || '',
          version: workflow.version || '1.0.0',
          triggers: workflow.triggers || [],
          steps: workflow.phases.flatMap(phase => 
            phase.tasks.map(task => ({
              id: `${phase.id}-${task.id}`,
              name: task.name,
              type: task.type || 'action',
              description: task.description
            }))
          ),
          outputs: []
        };
        
        // 生成可视化
        const vizOptions: VisualizationOptions = {
          format: options.format as any,
          showDetails: options.details,
          showConditions: options.conditions,
          colorize: options.color !== false,
          orientation: options.orientation as any
        };
        
        const content = visualizer.visualizeWorkflow(definition, vizOptions);
        
        // 输出结果
        if (options.output) {
          await visualizer.exportToFile(content, options.output, options.format);
        } else {
          console.log('\n' + content);
        }
        
      } catch (error: any) {
        logger.error('可视化工作流失败:', error.message);
      }
    });
  
  return command;
}

function createTasksCommand(): Command {
  const command = new Command('tasks');
  
  command
    .description('可视化任务列表和状态')
    .option('-f, --format <format>', '输出格式 (ascii|mermaid|graphviz|json)', 'ascii')
    .option('-s, --status <status>', '按状态过滤')
    .option('-d, --dependencies', '显示依赖关系')
    .option('-o, --output <file>', '输出到文件')
    .option('--no-color', '禁用颜色')
    .option('-O, --orientation <orientation>', '布局方向 (horizontal|vertical)', 'vertical')
    .action(async (options) => {
      try {
        const taskStore = new TaskStore();
        await taskStore.initialize();
        
        const visualizer = new WorkflowVisualizer();
        
        // 获取任务
        let tasks = await taskStore.getAllTasks();
        
        // 过滤任务
        if (options.status) {
          tasks = tasks.filter(t => t.status === options.status);
        }
        
        if (tasks.length === 0) {
          console.log(chalk.yellow('没有找到任务'));
          return;
        }
        
        // 生成可视化
        const vizOptions: VisualizationOptions = {
          format: options.format as any,
          showDependencies: options.dependencies,
          colorize: options.color !== false,
          orientation: options.orientation as any,
          showDetails: true
        };
        
        const content = visualizer.visualizeTaskDependencies(tasks, vizOptions);
        
        // 输出结果
        if (options.output) {
          await visualizer.exportToFile(content, options.output, options.format);
        } else {
          console.log('\n' + content);
        }
        
      } catch (error: any) {
        logger.error('可视化任务失败:', error.message);
      }
    });
  
  return command;
}

function createCollaborationCommand(): Command {
  const command = new Command('collaboration');
  
  command
    .description('可视化协作任务')
    .alias('collab')
    .argument('[collaborationId]', '协作任务 ID')
    .option('-f, --format <format>', '输出格式 (ascii|mermaid|graphviz|json)', 'ascii')
    .option('-d, --details', '显示详细信息')
    .option('-o, --output <file>', '输出到文件')
    .option('--no-color', '禁用颜色')
    .option('-O, --orientation <orientation>', '布局方向 (horizontal|vertical)', 'horizontal')
    .action(async (collaborationId, options) => {
      try {
        const agentManager = new AgentManager();
        await agentManager.initialize();
        
        const collaborationManager = new AgentCollaborationManager(agentManager);
        const visualizer = new WorkflowVisualizer();
        
        // 如果没有提供 ID，列出所有协作让用户选择
        if (!collaborationId) {
          const collaborations = collaborationManager.getCollaborations();
          
          if (collaborations.length === 0) {
            console.log(chalk.yellow('没有找到协作任务'));
            return;
          }
          
          const { selectedId } = await inquirer.prompt({
            type: 'list',
            name: 'selectedId',
            message: '选择要可视化的协作任务:',
            choices: collaborations.map(c => ({
              name: `${c.name} - ${c.status}`,
              value: c.id
            }))
          });
          
          collaborationId = selectedId;
        }
        
        // 获取协作任务
        const collaboration = collaborationManager.getCollaborationStatus(collaborationId);
        if (!collaboration) {
          console.log(chalk.red(`协作任务不存在: ${collaborationId}`));
          return;
        }
        
        // 生成可视化
        const vizOptions: VisualizationOptions = {
          format: options.format as any,
          showDetails: options.details,
          colorize: options.color !== false,
          orientation: options.orientation as any,
          showDependencies: true,
          showConditions: true
        };
        
        const content = visualizer.visualizeCollaboration(collaboration, vizOptions);
        
        // 输出结果
        if (options.output) {
          await visualizer.exportToFile(content, options.output, options.format);
        } else {
          console.log('\n' + content);
          
          // 显示协作信息
          console.log(chalk.bold('\n协作信息:'));
          console.log(`名称: ${collaboration.name}`);
          console.log(`类型: ${collaboration.type}`);
          console.log(`状态: ${collaboration.status}`);
          console.log(`创建时间: ${collaboration.createdAt.toLocaleString()}`);
          if (collaboration.completedAt) {
            console.log(`完成时间: ${collaboration.completedAt.toLocaleString()}`);
          }
        }
        
      } catch (error: any) {
        logger.error('可视化协作任务失败:', error.message);
      }
    });
  
  return command;
}

function createDependenciesCommand(): Command {
  const command = new Command('dependencies');
  
  command
    .description('可视化任务依赖关系图')
    .alias('deps')
    .option('-f, --format <format>', '输出格式 (ascii|mermaid|graphviz|json)', 'mermaid')
    .option('-t, --task <taskId>', '特定任务的依赖树')
    .option('--upstream', '显示上游依赖')
    .option('--downstream', '显示下游依赖')
    .option('-o, --output <file>', '输出到文件')
    .option('--no-color', '禁用颜色')
    .action(async (options) => {
      try {
        const taskStore = new TaskStore();
        await taskStore.initialize();
        
        const visualizer = new WorkflowVisualizer();
        const tasks = await taskStore.getAllTasks();
        
        if (tasks.length === 0) {
          console.log(chalk.yellow('没有找到任务'));
          return;
        }
        
        let filteredTasks = tasks;
        
        // 如果指定了任务，构建其依赖树
        if (options.task) {
          const targetTask = tasks.find(t => t.id === options.task);
          if (!targetTask) {
            console.log(chalk.red(`任务不存在: ${options.task}`));
            return;
          }
          
          const relatedTasks = new Set<string>();
          relatedTasks.add(targetTask.id);
          
          // 获取上游依赖
          if (options.upstream !== false) {
            const getUpstream = (taskId: string) => {
              const task = tasks.find(t => t.id === taskId);
              if (task?.dependencies) {
                task.dependencies.forEach(dep => {
                  relatedTasks.add(dep);
                  getUpstream(dep);
                });
              }
            };
            getUpstream(targetTask.id);
          }
          
          // 获取下游依赖
          if (options.downstream !== false) {
            tasks.forEach(task => {
              if (task.dependencies?.includes(targetTask.id)) {
                relatedTasks.add(task.id);
                // 递归获取下游
                const getDownstream = (taskId: string) => {
                  tasks.forEach(t => {
                    if (t.dependencies?.includes(taskId)) {
                      relatedTasks.add(t.id);
                      getDownstream(t.id);
                    }
                  });
                };
                getDownstream(task.id);
              }
            });
          }
          
          filteredTasks = tasks.filter(t => relatedTasks.has(t.id));
        }
        
        // 生成可视化
        const vizOptions: VisualizationOptions = {
          format: options.format as any,
          showDependencies: true,
          colorize: options.color !== false,
          showDetails: true
        };
        
        const content = visualizer.visualizeTaskDependencies(filteredTasks, vizOptions);
        
        // 输出结果
        if (options.output) {
          await visualizer.exportToFile(content, options.output, options.format);
        } else {
          console.log('\n' + content);
          
          // 显示统计信息
          const hasDeps = filteredTasks.filter(t => t.dependencies && t.dependencies.length > 0);
          console.log(chalk.bold('\n依赖统计:'));
          console.log(`总任务数: ${filteredTasks.length}`);
          console.log(`有依赖的任务: ${hasDeps.length}`);
          console.log(`独立任务: ${filteredTasks.length - hasDeps.length}`);
        }
        
      } catch (error: any) {
        logger.error('可视化依赖关系失败:', error.message);
      }
    });
  
  return command;
}